import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuthStore } from '@store'
import apiClient from '@services/api/client'
import PricingGrid from '@components/payment/PricingGrid'
import PaymentMethodSelector from '@components/payment/PaymentMethodSelector'
import OrderSummary from '@components/payment/OrderSummary'
import WaveQRModal from '@components/payment/WaveQRModal'

function PaymentArticlePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const { postData, postId, sessionToken, expiresIn, isEdit } = location.state || {}
  
  const [selectedMethod, setSelectedMethod] = useState('')
  const [waveUrl, setWaveUrl] = useState(null)
  const [showQR, setShowQR] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [countryCode, setCountryCode] = useState('+225') // Côte d'Ivoire par défaut
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFreeTrialActive, setIsFreeTrialActive] = useState(false)
  const [freeTrialDaysLeft, setFreeTrialDaysLeft] = useState(0)
  const [duration, setDuration] = useState(7)
  const [imageCount, setImageCount] = useState(location.state?.imageCount || 3)
  const [pricing, setPricing] = useState({
    1: 100,
    3: 300,
    7: 500,
    30: 2500
  })
  const [isLoadingPricing, setIsLoadingPricing] = useState(true)
  const [timeRemaining, setTimeRemaining] = useState('23:59:59')
  const [creationTime] = useState(() => new Date().getTime()) // Stocker l'heure de création avec lazy init

  const calculatePrice = () => {
    // Utiliser les prix chargés depuis l'API selon le nombre d'images
    let price = pricing[duration] || 500
    return price
  }

  const totalPrice = calculatePrice()

  useEffect(() => {
    // checkFreeTrial() // Désactivé temporairement
    loadPricing()
    
    // Timer de compte à rebours pour les 24h
    if (sessionToken) {
      const timer = setInterval(() => {
        const now = new Date().getTime()
        const expiry = creationTime + (24 * 60 * 60 * 1000) // 24 heures depuis la création
        const distance = expiry - now
        
        if (distance < 0) {
          setTimeRemaining('Expiré')
          clearInterval(timer)
          toast.error('Le délai de paiement a expiré', {
            duration: 5000,
            icon: '⏰'
          })
          setTimeout(() => navigate('/'), 3000)
        } else {
          const hours = Math.floor(distance / (1000 * 60 * 60))
          const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
          const seconds = Math.floor((distance % (1000 * 60)) / 1000)
          setTimeRemaining(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
        }
      }, 1000)
      
      return () => clearInterval(timer)
    }
  }, [user, navigate, sessionToken, creationTime])

  const loadPricing = async () => {
    try {
      setIsLoadingPricing(true)
      const result = await apiClient.get('/tarifs')
      if (result.success && result.data) {
        // imageCount 3→I3, 6→I6, 9→I9
        const pkgMap = { 3: 'I3', 6: 'I6', 9: 'I9' }
        const pkg = pkgMap[imageCount] || 'I3'
        const tarifs = result.data[pkg] || []
        const pricingData = {}
        tarifs.forEach(t => {
          pricingData[t.nb_jours] = parseFloat(t.montant_Tarifs)
        })
        if (Object.keys(pricingData).length > 0) {
          setPricing(pricingData)
        }
      }
    } catch (error) {
      console.error('Erreur chargement tarifs:', error)
      const defaultPricing = {
        3: { 1: 100, 3: 300, 7: 500, 30: 2500 },
        6: { 1: 200, 3: 500, 7: 1000, 30: 5000 },
        9: { 1: 300, 3: 700, 7: 2000, 30: 7000 }
      }
      setPricing(defaultPricing[imageCount] || defaultPricing[3])
    } finally {
      setIsLoadingPricing(false)
    }
  }

  const checkFreeTrial = async () => {
    try {
      await apiClient.get('/subscription/status')
    } catch {}
  }

  const handleFreeTrial = async () => {
    setIsProcessing(true)
    try {
      if (postId) {
        await apiClient.put(`/posts/${postId}`, { duration: 7, payment_status: 'free_trial' })
      }
      toast.success('Publication gratuite activée !')
      setTimeout(() => {
        navigate('/dashboard', {
          state: {
            paymentSuccess: true,
            message: isEdit ? 'Annonce mise à jour avec succès !' : 'Annonce publiée avec succès !',
            isFreeTrial: true
          }
        })
      }, 1500)
    } catch (error) {
      toast.error('Erreur lors de la publication.')
    } finally {
      setIsProcessing(false)
    }
  }



  const handlePayment = async () => {
    if (!selectedMethod) {
      toast.error('Veuillez sélectionner un moyen de paiement')
      return
    }

    setIsProcessing(true)
    try {
      if (selectedMethod === 'wave') {
        // Wave refuse localhost → en dev, définir VITE_PUBLIC_APP_URL (domaine public HTTPS).
        // En prod, window.location.origin est déjà public.
        const baseUrl = import.meta.env.VITE_PUBLIC_APP_URL || window.location.origin
        const successUrl = `${baseUrl}/payment-success?postId=${postId || ''}&duration=${duration}&type=article`
        const errorUrl   = `${baseUrl}/payment-error?postId=${postId || ''}&type=article`

        const result = await apiClient.post('/payment/wave', {
          amount:      totalPrice,
          success_url: successUrl,
          error_url:   errorUrl,
          post_id:     postId || null,
          duration:    duration,
        })

        if (result.success && result.wave_url) {
          if (result.dev_mode) {
            // Simulation : pas de vrai Wave → on suit directement le lien
            window.location.href = result.wave_url
            return
          }
          // Vrai Wave : afficher le QR code à scanner avec le téléphone
          setWaveUrl(result.wave_url)
          setShowQR(true)
        } else {
          toast.error(result.message || 'Impossible de créer la session Wave.')
        }
      }
    } catch (error) {
      console.error('Erreur paiement Wave:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la création du paiement.')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fefcf8] via-blue-50 to-purple-50 py-8">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#C49B2E] rounded-full mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-[#001F3F] mb-2">Finaliser votre publication</h1>
          <p className="text-gray-600 text-lg">Choisissez la durée et le mode de paiement</p>
        </div>

        {/* Timer d'expiration */}
        {sessionToken && timeRemaining !== 'Expiré' && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="p-5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl shadow-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-lg">⏰ Temps restant pour valider</p>
                    <p className="text-sm text-white/90">Votre annonce sera supprimée après expiration</p>
                  </div>
                </div>
                <div className="text-3xl font-bold font-mono bg-white/20 px-6 py-3 rounded-xl backdrop-blur-sm">
                  {timeRemaining}
                </div>
              </div>
            </div>
          </div>
        )}

        {isFreeTrialActive && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="p-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl shadow-xl text-white">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2">🎉 Période d'essai gratuite active!</h3>
                  <p className="text-white/90 text-lg mb-4">
                    Il vous reste <span className="font-bold text-yellow-300">{freeTrialDaysLeft} jours</span> d'essai gratuit.
                  </p>
                  <button onClick={handleFreeTrial} disabled={isProcessing}
                    className="w-full px-8 py-4 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-all shadow-lg disabled:opacity-50 transform hover:scale-105">
                    {isProcessing ? '⏳ Publication en cours...' : '✨ Publier gratuitement'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isFreeTrialActive && (
          <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Colonne principale - 2/3 */}
            <div className="lg:col-span-2 space-y-6">
              {/* Grille de tarification */}
              <PricingGrid
                pricing={pricing}
                selectedDuration={duration}
                onDurationChange={setDuration}
                imageCount={imageCount}
                type="article"
              />

              {/* Méthode de paiement */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <PaymentMethodSelector
                  selectedMethod={selectedMethod}
                  onMethodChange={setSelectedMethod}
                  phoneNumber={phoneNumber}
                  onPhoneNumberChange={setPhoneNumber}
                  countryCode={countryCode}
                  onCountryCodeChange={setCountryCode}
                />
              </div>
            </div>

            {/* Colonne récapitulatif - 1/3 */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 space-y-6">
                <OrderSummary
                  title={postData?.title || 'Article'}
                  category={postData?.categoryName || postData?.category || 'Catégorie'}
                  duration={duration}
                  price={totalPrice}
                  imageCount={imageCount}
                  type="article"
                  timeRemaining={sessionToken ? timeRemaining : null}
                />

                {/* Boutons d'action */}
                <div className="space-y-3">
                  <button
                    onClick={handlePayment}
                    disabled={!selectedMethod || isProcessing}
                    className="w-full px-6 py-4 bg-gradient-to-r from-[#D4AF37] to-[#C49B2E] text-white font-bold rounded-xl hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Traitement en cours...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Payer {totalPrice.toLocaleString('fr-FR')} FCFA
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => navigate(-1)} 
                    disabled={isProcessing}
                    className="w-full px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-all disabled:opacity-50"
                  >
                    ← Retour
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bouton retour pour la période d'essai */}
        {isFreeTrialActive && (
          <div className="max-w-4xl mx-auto">
            <button 
              type="button" 
              onClick={() => navigate(-1)} 
              disabled={isProcessing}
              className="w-full px-6 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all disabled:opacity-50 border-2 border-gray-200"
            >
              ← Retour
            </button>
          </div>
        )}
      </div>

      {showQR && waveUrl && (
        <WaveQRModal
          waveUrl={waveUrl}
          postId={postId}
          amount={totalPrice}
          onClose={() => setShowQR(false)}
        />
      )}
    </div>
  )
}

export default PaymentArticlePage
